import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
  alpha,
  Menu,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  History,
  Edit,
  Search,
  CheckCircleOutline,
  HourglassEmpty,
  Assignment,
  Error,
  SwapHoriz,
  PlayArrow,
  PictureAsPdf,
  ImageOutlined,
  SmartToy,
} from '@mui/icons-material';
import { caseService } from '../services/api';
import pdfGenerator from '../utils/pdfGenerator';
import CaseDetailsDialog from './CaseDetailsDialog';
import ProcessedCaseView from './ProcessedCaseView';
import { useTranslation } from 'react-i18next';
import { usePortalText } from '../locales/portalTranslations';

// Date formatting function instead of using date-fns
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const statusColors = {
  Open: { color: '#2962FF', backgroundColor: '#e3f2fd', borderColor: '#90caf9' },
  Pending: { color: '#2962FF', backgroundColor: '#e3f2fd', borderColor: '#90caf9' }, // Map to Open style
  Recommended: { color: '#4caf50', backgroundColor: '#e8f5e9', borderColor: '#a5d6a7' },
  'Not Recommended': { color: '#f44336', backgroundColor: '#ffebee', borderColor: '#ef9a9a' },
  Approved: { color: '#4caf50', backgroundColor: '#e8f5e9', borderColor: '#a5d6a7' }, // Map to Recommended style
  Rejected: { color: '#f44336', backgroundColor: '#ffebee', borderColor: '#ef9a9a' }, // Map to Not Recommended style
  Processed: { color: '#2e7d32', backgroundColor: '#e8f5e9', borderColor: '#a5d6a7' },
  Reassigned: { color: '#ed6c02', backgroundColor: '#fff3e0', borderColor: '#ffcc80' },
  Expired: { color: '#d32f2f', backgroundColor: '#ffebee', borderColor: '#ef9a9a' }
};

// Reason display mapping
const reasonMapping = {
  'Break-in': 'Vehicle Break-in Inspection',
  'Accident': 'Post-Accident Inspection',
  'Routine': 'Routine Maintenance Check',
  'Damage': 'Damage Assessment',
  'Compliance': 'Regulatory Compliance Check'
};

const statusIcons = {
  Open: <Assignment fontSize="small" />,
  Pending: <Assignment fontSize="small" />,
  Recommended: <CheckCircleOutline fontSize="small" />,
  'Not Recommended': <Error fontSize="small" />,
  Approved: <CheckCircleOutline fontSize="small" />,
  Rejected: <Error fontSize="small" />, // Using Error icon for consistency
  Processed: <CheckCircleOutline fontSize="small" />,
  Reassigned: <SwapHoriz fontSize="small" />,
  Expired: <Error fontSize="small" />
};

const CasesTable = ({ initialStatusFilter }) => {
  const { t, i18n } = useTranslation();
  const pt = usePortalText();
  const theme = useTheme();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCase, setSelectedCase] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('details');
  const [openProcessedView, setOpenProcessedView] = useState(false);
  const [actionConfirmationOpen, setActionConfirmationOpen] = useState(false);
  const [reassignmentDetailsOpen, setReassignmentDetailsOpen] = useState(false);
  const [reassignmentDetails, setReassignmentDetails] = useState([]);
  const [reassignmentDetailsLoading, setReassignmentDetailsLoading] = useState(false);
  const [reassignmentDetailsError, setReassignmentDetailsError] = useState('');

  // PDF download state
  const [pdfCaseItem, setPdfCaseItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [animate, setAnimate] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Animation effect when data loads
  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => setAnimate(true), 100);
    }
  }, [loading, error]);

  // Set initial status filter if provided
  useEffect(() => {
    if (initialStatusFilter && initialStatusFilter !== statusFilter) {
      setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        setError(null);
        let response;

        if (statusFilter === 'All') {
          response = await caseService.getAllCases();
        } else {
          response = await caseService.getCasesByStatus(statusFilter);
        }

        setCases(response.data.cases);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching cases:', err);
        setError(t('casesTable.errorLoading'));
        setLoading(false);
      }
    };

    fetchCases();
  }, [statusFilter, i18n.language, retryCount]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewCase = (caseItem, event) => {
    // Stop event propagation to prevent triggering the row click
    if (event) event.stopPropagation();

    setSelectedCase(caseItem);
    setDialogMode('details');
    setOpenDialog(true);
  };

  const handleViewHistory = (caseItem, event) => {
    event.stopPropagation();
    setSelectedCase(caseItem);
    setDialogMode('history');
    setOpenDialog(true);
  };


  const handleTakeAction = (caseItem, event) => {
    // Stop event propagation to prevent triggering the row click
    event.stopPropagation();
    setSelectedCase(caseItem);
    setActionConfirmationOpen(true);
  };

  const handleViewReassignment = async (caseItem, event) => {
    event.stopPropagation();
    setSelectedCase(caseItem);
    setReassignmentDetailsOpen(true);
    setReassignmentDetailsLoading(true);
    setReassignmentDetailsError('');
    try {
      const response = await caseService.getReassignedImages(caseItem.web_reference_number, Date.now());
      setReassignmentDetails(Array.isArray(response) ? response : []);
    } catch (error) {
      setReassignmentDetailsError(error.response?.data?.message || 'Unable to load reassignment details.');
    } finally {
      setReassignmentDetailsLoading(false);
    }
  };

  const confirmTakeAction = async () => {
    const caseItem = selectedCase;
    if (!caseItem) return;
    setActionConfirmationOpen(false);

    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const username = currentUser?.username || 'Unknown User';

      // Check if this user already has a lock in the current session
      const isCurrentSessionLock = window.sessionStorage.getItem(`case_lock_${caseItem.web_reference_number}`) === username;

      if (isCurrentSessionLock) {
        // User already has a session lock, allow them to continue without checking server
        console.log('User has a session lock for this case, continuing without server check');
        setOpenProcessedView(true);
        return;
      }

      // Check if the case is already locked on the server
      const lockStatus = await caseService.checkCaseLock(caseItem.web_reference_number);

      if (lockStatus.islocked) {
        // Check if the case is locked by the current user
        if (lockStatus.currentlockedby === username) {
          // Case is locked by the same user, allow them to continue
          console.log('Case already locked by the current user, continuing...');

          // Store the lock in session storage to track it across component re-renders
          window.sessionStorage.setItem(`case_lock_${caseItem.web_reference_number}`, username);

          setOpenProcessedView(true);
        } else {
          // Case is locked by someone else
          setSnackbarMessage(`This case is currently locked by ${lockStatus.currentlockedby}. Please try again later.`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } else {
        // Case is not locked, prompt the user
        setSnackbarMessage("Please lock the case to take action.");
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        setOpenProcessedView(true);
      }
    } catch (error) {
      console.error('Error checking case lock status:', error);
      setSnackbarMessage('Error checking case lock status. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEditCase = (caseItem, event) => {
    // Stop event propagation to prevent triggering the row click
    event.stopPropagation();
    setSelectedCase(caseItem);
    setOpenDialog(true);
  };

  const handleDownloadPdf = (caseItem, event) => {
    // Stop event propagation to prevent triggering the row click
    event.stopPropagation();
    setPdfCaseItem(caseItem);
    handleGeneratePdfReport(caseItem, false);
  };

  const handleDownloadAiPdf = (caseItem, event) => {
    event.stopPropagation();
    handleGeneratePdfReport(caseItem, true);
  };

  // handleCloseSnackbar is defined below

  const handleGeneratePdfReport = async (caseItem, aiReport = false) => {
    if (!caseItem) return;
    try {
      setSnackbarMessage(t('casesTable.pdfGenerating'));
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      // Call the backend API to generate the PDF
      const blob = aiReport
        ? await caseService.generateAiPdfReport(caseItem.web_reference_number)
        : await caseService.generatePdfReport(caseItem.web_reference_number);

      // Create a link to download the blob
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${aiReport ? 'ai_assessment_report' : 'inspection_report'}_${caseItem.web_reference_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbarMessage(t('casesTable.pdfSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      setSnackbarMessage(`Error generating PDF report: ${error.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseProcessedView = () => {
    setOpenProcessedView(false);
  };

  const handleViewImages = (caseItem, event) => {
    event?.stopPropagation();
    setSelectedCase(caseItem);
    setOpenProcessedView(true);
  };

  const statusLabel = (status) => {
    const keys = {
      Open: 'open', Pending: 'pending', Processed: 'processed', Reassigned: 'reassigned',
      Recommended: 'recommended', Approved: 'approved', Rejected: 'rejected', Expired: 'expired',
      'Not Recommended': 'notRecommended',
    };
    return keys[status] ? t(`casesTable.${keys[status]}`, { defaultValue: status }) : status;
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Filter cases based on search term
  const filteredCases = cases.filter(caseItem => {
    const searchLower = searchTerm.toLowerCase();
    return (
      caseItem.web_reference_number.toLowerCase().includes(searchLower) ||
      (caseItem.vehicle_plate && caseItem.vehicle_plate.toLowerCase().includes(searchLower)) ||
      (caseItem.make && caseItem.make.toLowerCase().includes(searchLower)) ||
      (caseItem.model && caseItem.model.toLowerCase().includes(searchLower)) ||
      (caseItem.chassis_number && caseItem.chassis_number.toLowerCase().includes(searchLower))
    );
  });

  // Pagination
  const paginatedCases = filteredCases.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
      }}>
        <Error color="error" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('casesTable.errorTryAgain')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setRetryCount(count => count + 1)}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      opacity: animate ? 1 : 0,
      transform: animate ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.4s ease'
    }}>
      <Paper sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        mb: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        background: theme.palette.background.paper,
        backdropFilter: 'blur(8px)',
        overflow: 'hidden'
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          pb: 1
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 40,
                height: 3,
                bgcolor: theme.palette.primary.main,
                borderRadius: 1.5
              }
            }}
          >
            {t('casesTable.title')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          mb: 3
        }}>
          <TextField
            placeholder={t('casesTable.searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              minWidth: 300,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.default, 1),
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(theme.palette.background.paper, 1),
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.default, 1)
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(theme.palette.background.paper, 1),
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }
            }}>
              <InputLabel>{t('casesTable.statusFilter')}</InputLabel>
              <Select value={statusFilter} label={t('casesTable.statusFilter')} onChange={handleStatusFilterChange}>
                <MenuItem value="All">{t('casesTable.allStatuses')}</MenuItem>
                <MenuItem value="Open">{t('casesTable.open')}</MenuItem>
                <MenuItem value="Recommended">{t('casesTable.recommended')}</MenuItem>
                <MenuItem value="Not Recommended">{t('casesTable.rejected')}</MenuItem>
                <MenuItem value="Processed">{t('casesTable.processed')}</MenuItem>
                <MenuItem value="Reassigned">{t('casesTable.reassigned')}</MenuItem>
                <MenuItem value="Expired">{t('casesTable.expired')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer sx={{ borderRadius: 1, '& .MuiTableCell-root': { borderColor: alpha(theme.palette.divider, 0.1) } }}>
          <Table sx={{ minWidth: 650 }} aria-label="cases table">
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04), '& .MuiTableCell-head': { color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.775rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5 } }}>
                <TableCell>{t('casesTable.referenceNumber')}</TableCell>
                <TableCell>{t('casesTable.vehicle')}</TableCell>
                <TableCell>{t('casesTable.plateNumber')}</TableCell>
                <TableCell>{t('casesTable.chassisNumber')}</TableCell>
                <TableCell>{t('casesTable.policyPeriod')}</TableCell>
                <TableCell>{t('casesTable.reason')}</TableCell>
                <TableCell>{t('casesTable.status')}</TableCell>
                <TableCell align="center">{t('casesTable.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCases.length > 0 ? (
                paginatedCases.map((caseItem, index) => (
                  <TableRow
                    key={caseItem.id}
                    onClick={() => handleViewCase(caseItem)}
                    sx={{
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02), cursor: 'pointer' },
                      backgroundColor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.background.default, 0.4),
                      '& .MuiTableCell-root': { py: 1.75, fontSize: '0.875rem' }
                    }}
                  >
                    <TableCell>
                      <Typography
                        component="button"
                        type="button"
                        variant="body2"
                        fontWeight={600}
                        color="primary.main"
                        onClick={event => handleViewCase(caseItem, event)}
                        aria-label={`Open case history for ${caseItem.web_reference_number}`}
                        sx={{ p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {caseItem.web_reference_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {`${caseItem.make} ${caseItem.model}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {caseItem.vehicle_plate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {caseItem.chassis_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {formatDate(caseItem.policy_start_date)} - {formatDate(caseItem.policy_expiry_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {caseItem.reason_for_inspection ?? "NULL"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel(caseItem.status)}
                        size="small"
                        icon={statusIcons[caseItem.status]}
                        sx={{
                          color: statusColors[caseItem.status]?.color,
                          backgroundColor: statusColors[caseItem.status]?.backgroundColor,
                          borderColor: statusColors[caseItem.status]?.borderColor,
                          border: '1px solid',
                          fontWeight: 600,
                          borderRadius: '6px',
                          py: 0.5,
                          '& .MuiChip-icon': {
                            color: statusColors[caseItem.status]?.color,
                            marginLeft: '4px',
                            marginRight: '-4px'
                          },
                          '& .MuiChip-label': {
                            px: 1.5,
                            fontSize: '0.75rem'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title={t('casesTable.caseHistory', { defaultValue: 'Case history' })} arrow>
                          <IconButton size="small" onClick={(e) => handleViewHistory(caseItem, e)} sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }, padding: '4px' }}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {caseItem.status === 'Processed' ? (
                          <Tooltip title={t('casesTable.takeAction')} arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleTakeAction(caseItem, e)}
                              sx={{
                                color: theme.palette.success.main,
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) },
                                padding: '4px'
                              }}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : caseItem.status === 'Reassigned' ? (
                          <Tooltip title="Reassignment details" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleViewReassignment(caseItem, e)}
                              aria-label={`View reassignment details for ${caseItem.web_reference_number}`}
                              sx={{
                                color: theme.palette.warning.dark,
                                bgcolor: alpha(theme.palette.warning.main, 0.12),
                                '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.22) },
                                padding: '4px'
                              }}
                            >
                              <SwapHoriz fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : ['Open', 'Pending'].includes(caseItem.status) ? (
                          <Tooltip title={t('casesTable.edit')} arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditCase(caseItem, e)}
                              sx={{
                                color: theme.palette.info.main,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) },
                                padding: '4px'
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {['Recommended', 'Not Recommended', 'Approved', 'Rejected'].includes(caseItem.status) && (
                          <>
                            <Tooltip title="View inspection images" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleViewImages(caseItem, e)}
                                sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }, padding: '4px' }}
                              >
                                <ImageOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download AI Assessment Report" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleDownloadAiPdf(caseItem, e)}
                                aria-label={`Download AI Assessment Report for ${caseItem.web_reference_number}`}
                                sx={{
                                  color: '#7b1fa2',
                                  bgcolor: alpha('#7b1fa2', 0.1),
                                  '&:hover': { bgcolor: alpha('#7b1fa2', 0.2) },
                                  padding: '4px'
                                }}
                              >
                                <SmartToy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('casesTable.downloadPdf')} arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleDownloadPdf(caseItem, e)}
                                aria-label={`Download inspection PDF for ${caseItem.web_reference_number}`}
                                sx={{
                                  color: '#d32f2f',
                                  bgcolor: alpha('#d32f2f', 0.1),
                                  '&:hover': { bgcolor: alpha('#d32f2f', 0.2) },
                                  padding: '4px'
                                }}
                              >
                                <PictureAsPdf fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Search sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.5) }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('casesTable.noCasesFound')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-select': {
              borderRadius: '6px',
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              px: 1
            },
            '& .MuiTablePagination-selectIcon': {
              color: theme.palette.text.secondary
            },
            '& .MuiTablePagination-actions': {
              '& .MuiIconButton-root': {
                borderRadius: '6px',
                backgroundColor: alpha(theme.palette.background.default, 0.6),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.default, 1)
                }
              }
            }
          }}
        />
      </Paper>

      {selectedCase && (
        <>
          <CaseDetailsDialog
            open={openDialog}
            onClose={handleCloseDialog}
            caseData={selectedCase}
            mode={dialogMode}
          />
          <ProcessedCaseView
            open={openProcessedView}
            onClose={handleCloseProcessedView}
            onStatusChanged={() => setRetryCount(count => count + 1)}
            caseData={selectedCase}
            readOnly={['Recommended', 'Not Recommended', 'Approved', 'Rejected'].includes(selectedCase.status)}
          />
          <Dialog
            open={actionConfirmationOpen}
            onClose={() => setActionConfirmationOpen(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle>{pt('takeActionTitle')}</DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pt('lockWarning')}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Other supervisors cannot take action on <strong>{selectedCase.web_reference_number}</strong> until you submit your decision, unlock the case, or the lock expires.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button color="inherit" onClick={() => setActionConfirmationOpen(false)}>{pt('cancel')}</Button>
              <Button variant="contained" onClick={confirmTakeAction}>{pt('lockContinue')}</Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={reassignmentDetailsOpen}
            onClose={() => setReassignmentDetailsOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6">{pt('reassignedPhotos')}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedCase.web_reference_number}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pt('reassignmentWindow')}
              </Alert>
              {reassignmentDetailsLoading ? (
                <Box sx={{ py: 5, display: 'grid', placeItems: 'center' }}><CircularProgress size={32} /></Box>
              ) : reassignmentDetailsError ? (
                <Alert severity="error">{reassignmentDetailsError}</Alert>
              ) : reassignmentDetails.length ? (
                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {reassignmentDetails.map((image, index) => {
                    const label = String(image.photoType || image.phototype || `Photo ${index + 1}`)
                      .replace(/[_-]+/g, ' ')
                      .replace(/\b\w/g, letter => letter.toUpperCase());
                    return (
                      <Paper key={image.id || `${label}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{ width: 38, height: 38, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: 'warning.light', color: 'warning.dark', flexShrink: 0 }}>
                            <ImageOutlined fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                              <Chip label={`${pt('version')} ${image.version}`} size="small" variant="outlined" />
                            </Box>
                            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                              {image.remark || pt('noUnderwriterRemark')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                              {pt('requestedBy')} {image.remarkedBy || pt('supervisor')}
                              {image.remarkedAt ? ` • ${new Date(image.remarkedAt).toLocaleString()}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info">{pt('noReassignedPhotos')}</Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button variant="contained" onClick={() => setReassignmentDetailsOpen(false)}>{pt('close')}</Button>
            </DialogActions>
          </Dialog>
          {/* PDF Menu removed - direct download implemented */}
          {/* Notification Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default CasesTable;

// Add these styles to index.css or App.css for smoother animations
// @keyframes fadeIn {
//   from { opacity: 0; transform: translateY(10px); }
//   to { opacity: 1; transform: translateY(0); }
// }
